import React from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@finitless/shared/components";
import { useTranslation } from "react-i18next";
import {
  Button,
  buttonVariants,
  Icon,
  IconButton,
  NativeSelect,
  Pagination,
  Skeleton,
  TextArea,
} from "../ui";
import { useAiAgents } from "../../hooks/useAiAgents";
import useAgentTranslations, {
  AVAILABLE_LOCALES,
  extractPlaceholders,
} from "../../hooks/useAgentTranslations";
import Modal from "../ui/UIElements/Modal";
import "./agent-translations-settings.css";

// Pagination constants - can be moved to a config file later
const TRANSLATIONS_PER_PAGE = 50;

const formatKeyLabel = (key) => {
  return key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
};

const AgentTranslationsSettingsPage = ({ selectedBrand }) => {
  const { t } = useTranslation("agentTranslations");
  const [selectedAgentId, setSelectedAgentId] = React.useState(null);
  const [activeLocale, setActiveLocale] = React.useState("en");
  const [editedValues, setEditedValues] = React.useState({});
  const [isCopyModalOpen, setIsCopyModalOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pendingSelection, setPendingSelection] = React.useState(null);
  const keepEditingRef = React.useRef(null);
  const searchInputRef = React.useRef(null);

  const { data: agentsData, isLoading: isLoadingAgents } = useAiAgents({
    pageSize: 50,
  });

  const {
    useTranslations,
    useUpdateTranslations,
    useCopyTranslations,
    useResetTranslation,
  } = useAgentTranslations();

  const { data: translations, isLoading: isLoadingTranslations } =
    useTranslations(selectedAgentId, activeLocale);

  const updateMutation = useUpdateTranslations();
  const copyMutation = useCopyTranslations();
  const resetMutation = useResetTranslation();

  const agents = agentsData?.agents || [];
  const selectedAgent = agents.find((a) => a.id === selectedAgentId);

  const hasUnsavedChanges = Object.keys(editedValues).length > 0;

  const translationKeys = Object.keys(translations || {});

  // Filter translations based on search query
  const filteredTranslationKeys = React.useMemo(() => {
    if (!searchQuery.trim()) return translationKeys;

    const query = searchQuery.toLowerCase().trim();
    return translationKeys.filter((key) => {
      const translation = translations?.[key];
      const keyMatch = key.toLowerCase().includes(query);
      const formattedKeyMatch = formatKeyLabel(key)
        .toLowerCase()
        .includes(query);
      const valueMatch = translation?.value?.toLowerCase().includes(query);
      const defaultMatch = translation?.default?.toLowerCase().includes(query);
      return keyMatch || formattedKeyMatch || valueMatch || defaultMatch;
    });
  }, [translationKeys, translations, searchQuery]);

  // Pagination - simulating backend pagination structure for future compatibility
  const pagination = React.useMemo(() => {
    const total = filteredTranslationKeys.length;
    const totalPages = Math.ceil(total / TRANSLATIONS_PER_PAGE);
    return {
      total,
      total_pages: totalPages,
      page: currentPage,
      page_size: TRANSLATIONS_PER_PAGE,
    };
  }, [filteredTranslationKeys.length, currentPage]);

  // Get paginated translation keys for current page
  const paginatedTranslationKeys = React.useMemo(() => {
    const startIndex = (currentPage - 1) * TRANSLATIONS_PER_PAGE;
    const endIndex = startIndex + TRANSLATIONS_PER_PAGE;
    return filteredTranslationKeys.slice(startIndex, endIndex);
  }, [filteredTranslationKeys, currentPage]);

  // Reset to page 1 when search changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Check if any field exceeds maxLength
  const hasValidationErrors = Object.entries(editedValues).some(
    ([key, value]) => {
      const maxLength = translations?.[key]?.maxLength || 500;
      return value && value.length > maxLength;
    },
  );

  const handleAgentChange = (e) => {
    const newAgentId = e.target.value ? parseInt(e.target.value, 10) : null;
    if (hasUnsavedChanges) {
      setPendingSelection({ type: "agent", value: newAgentId });
      return;
    }
    setEditedValues({});
    setCurrentPage(1);
    setSelectedAgentId(newAgentId);
  };

  const handleLocaleChange = (e) => {
    const newLocale = e.target.value;
    if (hasUnsavedChanges) {
      setPendingSelection({ type: "locale", value: newLocale });
      return;
    }
    setEditedValues({});
    setCurrentPage(1);
    setActiveLocale(newLocale);
  };

  const handleKeepEditing = () => {
    setPendingSelection(null);
  };

  const handleConfirmDiscardAndSwitch = () => {
    if (!pendingSelection) return;

    setEditedValues({});
    setCurrentPage(1);
    if (pendingSelection.type === "agent") {
      setSelectedAgentId(pendingSelection.value);
    } else {
      setActiveLocale(pendingSelection.value);
    }
    setPendingSelection(null);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFieldChange = (key, value) => {
    setEditedValues((prev) => {
      const originalValue = translations?.[key]?.value || "";
      if (value === originalValue) {
        const newValues = { ...prev };
        delete newValues[key];
        return newValues;
      }
      return { ...prev, [key]: value };
    });
  };

  const handleResetField = (key) => {
    resetMutation.mutate(
      {
        agentId: selectedAgentId,
        locale: activeLocale,
        key,
      },
      {
        onSuccess: () => {
          setEditedValues((prev) => {
            const newValues = { ...prev };
            delete newValues[key];
            return newValues;
          });
        },
      },
    );
  };

  const handleSaveAll = () => {
    if (!selectedAgentId || Object.keys(editedValues).length === 0) return;

    updateMutation.mutate(
      {
        agentId: selectedAgentId,
        translations: {
          [activeLocale]: editedValues,
        },
      },
      {
        onSuccess: () => {
          setEditedValues({});
        },
      },
    );
  };

  const handleDiscardChanges = () => {
    setEditedValues({});
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    searchInputRef.current?.focus();
  };

  const handleCopyFromAgent = (sourceAgentId) => {
    if (!selectedAgentId || !sourceAgentId) return;

    copyMutation.mutate(
      {
        targetAgentId: selectedAgentId,
        sourceAgentId,
      },
      {
        onSuccess: () => {
          setIsCopyModalOpen(false);
          setEditedValues({});
        },
      },
    );
  };

  const getAgentLabel = (agent) => {
    const provider = agent.provider || t("labels.unknownProvider");
    const identifier =
      agent.provider_external_id ||
      agent.site?.domain ||
      t("labels.agentWithId", { id: agent.id });
    return `${provider.charAt(0).toUpperCase() + provider.slice(1)} - ${identifier}`;
  };

  return (
    <div
      className="ordering-agent-translations-page"
      data-ordering-surface="agent-translations"
    >
      <PageHeader
        title={t("header.title")}
        subtitle={t("header.subtitle")}
        selectedBrand={selectedBrand}
      />

      <div className="space-y-6">
        {/* Agent and Language Selectors */}
        <div className="ordering-agent-translations-page__selector-panel border-border bg-card rounded-lg border p-6">
          {isLoadingAgents ? (
            <div className="flex flex-col gap-4 sm:flex-row">
              <div className="flex-1">
                <Skeleton className="mb-3 h-4 w-24" />
                <Skeleton className="h-11 w-full rounded-lg" />
              </div>
              <div className="w-full sm:w-48">
                <Skeleton className="mb-3 h-4 w-20" />
                <Skeleton className="h-11 w-full rounded-lg" />
              </div>
            </div>
          ) : agents.length === 0 ? (
            <div className="py-8 text-center">
              <div className="bg-muted mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full">
                <Icon name="Bot" className="text-muted-foreground" size={32} />
              </div>
              <h3 className="text-foreground mb-2 text-lg font-bold">
                {t("empty.noAgents.title")}
              </h3>
              <p className="text-muted-foreground mb-4 text-sm">
                {t("empty.noAgents.subtitle")}
              </p>
              <Link
                to="/ai-agents"
                className={buttonVariants({
                  variant: "default",
                  size: "touch",
                })}
              >
                <Icon name="Plus" size={16} />
                {t("actions.createAgent")}
              </Link>
            </div>
          ) : (
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
              {/* Agent Selector */}
              <div className="w-full sm:flex-1">
                <label
                  htmlFor="agent-selector"
                  className="text-foreground mb-3 block text-sm font-medium"
                >
                  {t("selectors.agent")}
                </label>
                <NativeSelect
                  id="agent-selector"
                  value={selectedAgentId || ""}
                  onChange={handleAgentChange}
                  className="custom-select border-input bg-background text-foreground focus:ring-ring h-11 w-full rounded-sm border px-4 font-medium focus:ring-2 focus:outline-none"
                >
                  <option value="">{t("selectors.selectAgent")}</option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {getAgentLabel(agent)}
                    </option>
                  ))}
                </NativeSelect>
              </div>

              {/* Language Selector */}
              <div className="w-full sm:w-48">
                <label
                  htmlFor="locale-selector"
                  className="text-foreground mb-3 block text-sm font-medium"
                >
                  {t("selectors.language")}
                </label>
                <NativeSelect
                  id="locale-selector"
                  value={activeLocale}
                  onChange={handleLocaleChange}
                  className="custom-select border-input bg-background text-foreground focus:ring-ring h-11 w-full rounded-sm border px-4 font-medium focus:ring-2 focus:outline-none"
                >
                  {AVAILABLE_LOCALES.map((locale) => (
                    <option key={locale.code} value={locale.code}>
                      {t(`locales.${locale.code}`, {
                        defaultValue: locale.label,
                      })}
                    </option>
                  ))}
                </NativeSelect>
              </div>
            </div>
          )}

          {/* Copy from Another Agent Button */}
          {selectedAgentId && agents.length > 0 && (
            <div className="border-border mt-4 border-t pt-4">
              <Button
                type="button"
                onClick={() => setIsCopyModalOpen(true)}
                emphasis="outline"
                intent="neutral"
                size="touch"
                className="ordering-agent-translations-page__secondary-action flex items-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium"
              >
                <Icon name="Copy" size={16} />
                {t("actions.copyFromAnotherAgent")}
              </Button>
            </div>
          )}
        </div>

        {/* Content when agent is selected */}
        {selectedAgentId && (
          <>
            {/* Search Bar */}
            {!isLoadingTranslations && translationKeys.length > 0 && (
              <div className="ordering-agent-translations-page__search relative">
                <div className="ordering-agent-translations-page__search-icon pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
                  <Icon
                    name="Search"
                    className="text-muted-foreground"
                    size={18}
                  />
                </div>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t("search.placeholder")}
                  aria-label={t("search.placeholder")}
                  className="ordering-agent-translations-page__search-input border-input bg-background text-foreground placeholder:text-muted-foreground focus:ring-ring h-11 w-full rounded-sm border pr-10 pl-11 font-medium focus:ring-2 focus:outline-none"
                />
                {searchQuery && (
                  <IconButton
                    type="button"
                    onClick={handleClearSearch}
                    aria-label={t("actions.clearSearch")}
                    emphasis="ghost"
                    intent="neutral"
                    size="iconTouch"
                    className="ordering-agent-translations-page__search-clear text-muted-foreground hover:text-foreground absolute inset-y-0 right-0 flex items-center pr-4 transition-colors"
                  >
                    <Icon name="X" size={16} />
                  </IconButton>
                )}
              </div>
            )}

            {/* Search Results Count */}
            {searchQuery && !isLoadingTranslations && (
              <div
                className="ordering-agent-translations-page__search-result flex items-center gap-2 text-sm"
                role="status"
                aria-live="polite"
              >
                <span className="text-muted-foreground">
                  {t("search.results", {
                    shown: filteredTranslationKeys.length,
                    total: translationKeys.length,
                  })}
                </span>
                {filteredTranslationKeys.length === 0 && (
                  <span className="text-warning">{t("search.noMatches")}</span>
                )}
              </div>
            )}

            {/* Translations List */}
            {isLoadingTranslations ? (
              <div className="space-y-2">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div
                    key={i}
                    className="ordering-agent-translations-page__translation-row border-border bg-card animate-pulse rounded-md border px-4 py-3"
                  >
                    <div className="grid gap-3 md:grid-cols-[minmax(180px,220px)_minmax(0,1fr)_auto] md:items-center">
                      <Skeleton className="h-4 w-36" />
                      <Skeleton className="h-10 w-full rounded-lg" />
                      <Skeleton className="h-6 w-24 rounded-lg" />
                    </div>
                  </div>
                ))}
              </div>
            ) : translationKeys.length === 0 ? (
              <div className="ordering-agent-translations-page__state border-border bg-card rounded-lg border p-8 text-center">
                <Icon
                  name="MessageSquare"
                  className="text-muted-foreground mx-auto mb-4"
                  size={40}
                />
                <h3 className="text-foreground mb-2 text-lg font-bold">
                  {t("empty.noTranslations.title")}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {t("empty.noTranslations.subtitle")}
                </p>
              </div>
            ) : filteredTranslationKeys.length === 0 ? (
              <div className="ordering-agent-translations-page__state border-border bg-card rounded-lg border p-8 text-center">
                <Icon
                  name="SearchX"
                  className="text-muted-foreground mx-auto mb-4"
                  size={40}
                />
                <h3 className="text-foreground mb-2 text-lg font-bold">
                  {t("empty.noSearchResults.title")}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {t("empty.noSearchResults.subtitle", {
                    query: searchQuery,
                  })}
                </p>
                <Button
                  type="button"
                  onClick={handleClearSearch}
                  emphasis="outline"
                  intent="neutral"
                  size="touch"
                  className="ordering-agent-translations-page__secondary-action mt-4 rounded-md px-4 py-2 text-sm font-medium"
                >
                  {t("actions.clearSearch")}
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {paginatedTranslationKeys.map((key) => {
                  const translation = translations[key];
                  const currentValue =
                    editedValues[key] ?? translation?.value ?? "";
                  const charCount = currentValue?.length || 0;
                  const maxLength = translation?.maxLength || 500;
                  const isOverLimit = charCount > maxLength;
                  const characterCountId = `translation-${key.replace(
                    /[^a-zA-Z0-9_-]/g,
                    "-",
                  )}-character-count`;
                  const placeholders = extractPlaceholders(
                    translation?.default,
                  );
                  const isEdited = key in editedValues;
                  const isResetting =
                    resetMutation.isPending &&
                    resetMutation.variables?.key === key;

                  return (
                    <div
                      key={key}
                      data-testid="translation-row"
                      data-translation-state={
                        isEdited
                          ? "modified"
                          : translation?.isCustom
                            ? "custom"
                            : "default"
                      }
                      className="ordering-agent-translations-page__translation-row border-border bg-card rounded-md border px-3 py-3 md:px-4"
                    >
                      <div className="grid gap-3 md:grid-cols-[minmax(180px,220px)_minmax(0,1fr)_auto] md:items-center md:gap-4">
                        <div className="flex min-w-0 items-center gap-2">
                          <h4
                            className="text-foreground truncate text-sm font-semibold"
                            title={formatKeyLabel(key)}
                          >
                            {formatKeyLabel(key)}
                          </h4>
                          {placeholders.length > 0 && (
                            <span
                              className="bg-muted text-muted-foreground rounded-full px-2 py-0.5 text-[10px] font-semibold"
                              title={`${t("labels.placeholders")} ${placeholders.join(", ")}`}
                            >
                              {t("labels.placeholders")} {placeholders.length}
                            </span>
                          )}
                          {translation?.isCustom && !isEdited && (
                            <span className="bg-accent text-primary rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase">
                              {t("badges.custom")}
                            </span>
                          )}
                          {isEdited && (
                            <span className="text-warning rounded-full bg-[var(--warning-bg)] px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase">
                              {t("badges.modified")}
                            </span>
                          )}
                        </div>

                        <TextArea
                          value={currentValue}
                          aria-label={formatKeyLabel(key)}
                          aria-invalid={isOverLimit || undefined}
                          aria-describedby={
                            isOverLimit ? characterCountId : undefined
                          }
                          onChange={(e) =>
                            handleFieldChange(key, e.target.value)
                          }
                          placeholder={translation?.default || ""}
                          rows={1}
                          className={`ordering-agent-translations-page__textarea bg-background text-foreground placeholder:text-muted-foreground focus:ring-ring h-11 min-h-11 w-full resize-none rounded-sm px-3 py-2 text-sm font-medium transition-colors focus:ring-2 focus:outline-none ${
                            isOverLimit
                              ? "border-error focus:ring-error border-2"
                              : "border-input border"
                          }`}
                        />

                        <div className="flex flex-wrap items-center gap-2 md:justify-end">
                          <span
                            id={characterCountId}
                            className={`text-xs font-semibold ${isOverLimit ? "text-error" : "text-muted-foreground"}`}
                          >
                            {charCount} / {maxLength}
                          </span>
                          {translation?.isCustom && !isEdited && (
                            <Button
                              type="button"
                              onClick={() => handleResetField(key)}
                              disabled={isResetting}
                              aria-busy={isResetting || undefined}
                              emphasis="outline"
                              intent="neutral"
                              className="ordering-agent-translations-page__secondary-action ordering-agent-translations-page__reset-action flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium disabled:opacity-50"
                            >
                              {isResetting ? (
                                <Icon
                                  name="Loader2"
                                  className="animate-spin"
                                  size={12}
                                />
                              ) : (
                                <Icon name="RotateCcw" size={12} />
                              )}
                              {t("actions.resetToDefault")}
                            </Button>
                          )}
                        </div>
                      </div>

                      {translation?.default &&
                        currentValue !== translation.default && (
                          <p
                            className="text-muted-foreground mt-2 truncate text-xs"
                            title={`${t("labels.defaultValue")}: ${translation.default}`}
                          >
                            {t("labels.defaultValue")}: {translation.default}
                          </p>
                        )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="ordering-agent-translations-page__pagination border-border bg-card flex flex-col items-center justify-between gap-4 rounded-md border p-4 sm:flex-row">
                <div className="text-muted-foreground text-sm">
                  {t("pagination.showing", {
                    from: (currentPage - 1) * TRANSLATIONS_PER_PAGE + 1,
                    to: Math.min(
                      currentPage * TRANSLATIONS_PER_PAGE,
                      pagination.total,
                    ),
                    total: pagination.total,
                  })}
                </div>
                <Pagination
                  currentPage={currentPage}
                  totalPages={pagination.total_pages}
                  onPageChange={handlePageChange}
                  maxVisiblePages={5}
                />
              </div>
            )}

            {/* Spacer for sticky footer */}
            {hasUnsavedChanges && <div className="h-20" />}
          </>
        )}
      </div>

      {/* Sticky Footer Bar for Unsaved Changes */}
      {hasUnsavedChanges && (
        <div
          className="ordering-agent-translations-page__unsaved border-warning bg-card sticky bottom-0 -mx-3 border-t px-3 shadow-[var(--od-shadow-2)] sm:-mx-4 sm:px-4"
          style={{ zIndex: "var(--layer-sticky)" }}
        >
          <div className="ordering-agent-translations-page__unsaved-inner py-4 pr-16">
            <div className="ordering-agent-translations-page__unsaved-layout flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="ordering-agent-translations-page__unsaved-indicator bg-warning h-2 w-2 animate-pulse rounded-full" />
                <span className="text-warning text-sm font-medium">
                  {t("unsaved.message")}
                </span>
              </div>
              <div className="ordering-agent-translations-page__unsaved-actions flex items-center gap-3">
                <Button
                  type="button"
                  onClick={handleDiscardChanges}
                  emphasis="outline"
                  intent="neutral"
                  size="touch"
                  className="ordering-agent-translations-page__secondary-action rounded-md px-4 py-2 text-sm font-medium sm:px-6"
                >
                  {t("actions.discard")}
                </Button>
                <Button
                  type="button"
                  onClick={handleSaveAll}
                  disabled={
                    updateMutation.isPending ||
                    hasValidationErrors ||
                    Object.keys(editedValues).length === 0
                  }
                  aria-busy={updateMutation.isPending || undefined}
                  emphasis="solid"
                  intent="brand"
                  size="touch"
                  className="ordering-agent-translations-page__primary-action flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50 sm:px-6"
                >
                  {updateMutation.isPending && (
                    <Icon name="Loader2" className="animate-spin" size={16} />
                  )}
                  {t("actions.saveChanges")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Copy Translations Modal */}
      {isCopyModalOpen && (
        <CopyTranslationsModal
          isOpen={isCopyModalOpen}
          onClose={() => setIsCopyModalOpen(false)}
          onConfirm={handleCopyFromAgent}
          targetAgent={selectedAgent}
          availableAgents={agents.filter((a) => a.id !== selectedAgentId)}
          isPending={copyMutation.isPending}
          getAgentLabel={getAgentLabel}
          t={t}
        />
      )}

      <Modal
        isOpen={Boolean(pendingSelection)}
        onClose={handleKeepEditing}
        title={t("unsaved.confirmDiscard")}
        showCloseButton={false}
        initialFocusRef={keepEditingRef}
        maxWidth="max-w-md"
      >
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button
            ref={keepEditingRef}
            type="button"
            onClick={handleKeepEditing}
            emphasis="outline"
            intent="neutral"
            size="touch"
            className="ordering-agent-translations-page__secondary-action rounded-md px-4 py-2.5 text-sm font-medium"
          >
            {t("actions.cancel")}
          </Button>
          <Button
            type="button"
            onClick={handleConfirmDiscardAndSwitch}
            emphasis="solid"
            intent="danger"
            size="touch"
            className="ordering-agent-translations-page__danger-action rounded-md px-4 py-2.5 text-sm font-medium"
          >
            {t("actions.discard")}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

const CopyTranslationsModal = ({
  isOpen,
  onClose,
  onConfirm,
  targetAgent,
  availableAgents,
  isPending,
  getAgentLabel,
  t,
}) => {
  const [sourceAgentId, setSourceAgentId] = React.useState(null);

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("modal.copy.title")}
      closeButtonLabel={t("actions.cancel")}
      maxWidth="max-w-md"
      className="ordering-agent-translations-page__modal"
    >
      <div className="mb-6">
        <p className="text-muted-foreground mb-3 text-sm">
          {t("modal.copy.subtitle")}
        </p>
        <div className="ordering-agent-translations-page__modal-warning border-error rounded-md border bg-[var(--error-bg)] p-3">
          <div className="flex items-start gap-2">
            <Icon
              name="AlertTriangle"
              className="text-error mt-0.5 flex-shrink-0"
              size={16}
            />
            <p className="text-error text-sm">
              <span className="font-bold">{t("modal.copy.warningLabel")}</span>{" "}
              {t("modal.copy.warningStart")}{" "}
              <span className="font-bold">
                {t("modal.copy.warningReplace")}
              </span>{" "}
              {t("modal.copy.warningMiddle")}{" "}
              <span className="text-foreground font-bold">
                {getAgentLabel(targetAgent)}
              </span>
              . {t("modal.copy.warningEnd")}
            </p>
          </div>
        </div>
      </div>

      {availableAgents.length === 0 ? (
        <div className="py-6 text-center">
          <Icon
            name="AlertCircle"
            className="text-muted-foreground mx-auto mb-3"
            size={32}
          />
          <p className="text-muted-foreground text-sm">
            {t("modal.copy.noAgents")}
          </p>
        </div>
      ) : (
        <>
          <label
            htmlFor="source-agent"
            className="text-foreground mb-3 block text-sm font-medium"
          >
            {t("modal.copy.sourceAgent")}
          </label>
          <NativeSelect
            id="source-agent"
            value={sourceAgentId || ""}
            onChange={(e) =>
              setSourceAgentId(
                e.target.value ? parseInt(e.target.value, 10) : null,
              )
            }
            className="ordering-agent-translations-page__modal-select border-input bg-background text-foreground focus:ring-ring mb-6 h-11 w-full rounded-sm border px-4 font-medium focus:ring-2 focus:outline-none"
          >
            <option value="">{t("selectors.selectAgent")}</option>
            {availableAgents.map((agent) => (
              <option key={agent.id} value={agent.id}>
                {getAgentLabel(agent)}
              </option>
            ))}
          </NativeSelect>

          <div className="flex items-center gap-3">
            <Button
              type="button"
              onClick={onClose}
              emphasis="outline"
              intent="neutral"
              size="touch"
              className="ordering-agent-translations-page__secondary-action flex-1 rounded-md px-4 py-2.5 text-sm font-medium"
            >
              {t("actions.cancel")}
            </Button>
            <Button
              type="button"
              onClick={() => onConfirm(sourceAgentId)}
              disabled={!sourceAgentId || isPending}
              aria-busy={isPending || undefined}
              emphasis="solid"
              intent="danger"
              size="touch"
              className="ordering-agent-translations-page__danger-action flex flex-1 items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isPending && (
                <Icon name="Loader2" className="animate-spin" size={16} />
              )}
              {t("actions.copyTranslations")}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default AgentTranslationsSettingsPage;
